import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router";
import { DefaultProviders } from "./components/providers/default.tsx";
import { useServiceWorker } from "./hooks/use-service-worker.ts";
import LocaleWrapper from "./components/providers/locale-wrapper.tsx";
import PublicIntegrations from "./components/providers/public-integrations.tsx";
import NetworkAnalytics from "./components/providers/network-analytics.tsx";
import BrandFavicon from "./components/providers/brand-favicon.tsx";
import {
  RuntimeLoadingFallback,
  RuntimeNavigationObserver,
} from "./components/providers/runtime-observability.tsx";
import ImpersonationBanner from "./components/layout/impersonation-banner.tsx";
import SiteLayout from "./components/layout/site-layout.tsx";
import {
  ROUTE_SLUGS,
  ENERGY_COMPARATOR_SLUGS,
  type RouteKey,
  FORUM_SUBPATH,
  localizedPath,
} from "./lib/routes.ts";
import {
  SAVED_OR_DEFAULT_LOCALE,
  isSupportedLocale,
  setLocaleInPath,
  SUPPORTED_LOCALES_ARRAY,
} from "./i18n";
import "./i18n";
// Home stays eagerly loaded: it is the landing page and its hero is the LCP
// element, so we avoid an extra async chunk fetch on first paint.
import HomePage from "./pages/home/page.tsx";

// CRM back office: lazily loaded so public visitors never download the ~25
// admin pages. These are only reached by authenticated staff under /crm.
const SupportWidgetPage = lazy(() => import("./components/support-chat"));
const CrmGuard = lazy(() => import("./pages/crm/_components/crm-guard.tsx"));
const CrmLayout = lazy(() => import("./pages/crm/_components/crm-layout.tsx"));
const CrmDashboardPage = lazy(() => import("./pages/crm/dashboard-page.tsx"));
const CrmNetworkAnalyticsPage = lazy(() => import("./pages/crm/network-analytics-page.tsx"));
const SoftwarePublicPage = lazy(() => import("./pages/software/page.tsx"));
const CrmSoftwarePage = lazy(() => import("./pages/crm/software-page.tsx"));
const CrmLeadsPage = lazy(() => import("./pages/crm/leads-page.tsx"));
const ContactRequestsPage = lazy(() => import("./pages/crm/contact-requests-page.tsx"));
const CrmJobApplicationsPage = lazy(
  () => import("./pages/crm/job-applications-page.tsx"),
);
const CrmCompanyDetailPage = lazy(
  () => import("./pages/crm/company-detail-page.tsx"),
);
const CrmProjectsPage = lazy(
  () => import("./pages/crm/website-projects-page.tsx"),
);
const CrmWebsiteProjectDetailPage = lazy(
  () => import("./pages/crm/website-project-detail-page.tsx"),
);
const CrmCalendarPage = lazy(() => import("./pages/crm/calendar-page.tsx"));
const CrmChatPage = lazy(() => import("./pages/crm/chat-page.tsx"));
const CrmUsersPage = lazy(() => import("./pages/crm/users-page.tsx"));
const CrmRolesPage = lazy(() => import("./pages/crm/roles-page.tsx"));
const CrmHeroAnimationPage = lazy(
  () => import("./pages/crm/hero-animation-page.tsx"),
);
const CrmThemePage = lazy(() => import("./pages/crm/theme-page.tsx"));
const CrmPortfolioPage = lazy(() => import("./pages/crm/portfolio-page.tsx"));
const CrmServicesPage = lazy(() => import("./pages/crm/services-page.tsx"));
const CrmClientShopsPage = lazy(
  () => import("./pages/crm/client-shops-page.tsx"),
);
const CrmSettingsPage = lazy(() => import("./pages/crm/crm-settings-page.tsx"));
const CrmMigrationPage = lazy(() => import("./pages/crm/migration-page.tsx"));
const CrmFinancePage = lazy(() => import("./pages/crm/finance-page.tsx"));
const CrmRevenuePage = lazy(() =>
  import("./pages/crm/finance-page.tsx").then((module) => ({
    default: module.CrmRevenuePage,
  })),
);
const CrmPayoutsPage = lazy(() =>
  import("./pages/crm/finance-page.tsx").then((module) => ({
    default: module.CrmPayoutsPage,
  })),
);
const CrmWalletPage = lazy(() => import("./pages/crm/wallet-page.tsx"));
const CrmClientServicesPage = lazy(
  () => import("./pages/crm/client-services-page.tsx"),
);
const CrmClientProjectsPage = lazy(
  () => import("./pages/crm/client-projects-page.tsx"),
);
const CrmTrainingPage = lazy(() => import("./pages/crm/training-page.tsx"));
const CrmCoworkingPage = lazy(() => import("./pages/crm/coworking-page.tsx"));
const CrmProductionPage = lazy(() => import("./pages/crm/production-page.tsx"));
const CrmPartnerContractsPage = lazy(
  () => import("./pages/crm/partner-contracts-page.tsx"),
);
const CrmEnergyComparatorAccessPage = lazy(
  () => import("./pages/crm/energy-comparator-access-page.tsx"),
);
const CrmCustomerToolPage = lazy(
  () => import("./pages/crm/customer-tool-page.tsx"),
);
const CrmCustomerOrdersPage = lazy(
  () => import("./pages/crm/customer-orders-page.tsx"),
);
const CrmSoftwareLicenseDeliveriesPage = lazy(
  () => import("./pages/crm/software-license-deliveries-page.tsx"),
);
const CrmTechnicalAssistancePage = lazy(
  () => import("./pages/crm/technical-assistance-page.tsx"),
);
const CrmDigitalAssistancePage = lazy(
  () => import("./pages/crm/digital-assistance-page.tsx"),
);
const CrmToolsPage = lazy(() => import("./pages/crm/tools-page.tsx"));
const CrmCmsPage = lazy(() => import("./pages/crm/cms-page.tsx"));
const CrmCmsSeoPage = lazy(() => import("./pages/crm/cms-seo-page.tsx"));
const CrmCmsRedirectsPage = lazy(
  () => import("./pages/crm/cms-redirects-page.tsx"),
);
const CrmCmsForumPage = lazy(() => import("./pages/crm/cms-forum-page.tsx"));
const CrmContentTextsPage = lazy(() =>
  import("./pages/crm/cms-content-editor-page.tsx").then((module) => ({
    default: module.CrmContentTextsPage,
  })),
);
const CrmContentSeoMetadataPage = lazy(() =>
  import("./pages/crm/cms-content-editor-page.tsx").then((module) => ({
    default: module.CrmContentSeoMetadataPage,
  })),
);
const CrmIntegrationsPage = lazy(
  () => import("./pages/crm/integrations-page.tsx"),
);
const CrmCatalogPage = lazy(() => import("./pages/crm/catalog-page.tsx"));
const CrmCatalogSuppliersPage = lazy(() =>
  import("./pages/crm/catalog-page.tsx").then((module) => ({
    default: module.CrmCatalogSuppliersPage,
  })),
);
const CrmCatalogCategoriesPage = lazy(() =>
  import("./pages/crm/catalog-page.tsx").then((module) => ({
    default: module.CrmCatalogCategoriesPage,
  })),
);
const CrmCatalogTaxonomiesPage = lazy(() =>
  import("./pages/crm/catalog-page.tsx").then((module) => ({
    default: module.CrmCatalogTaxonomiesPage,
  })),
);
const CrmCatalogMappingsPage = lazy(() =>
  import("./pages/crm/catalog-page.tsx").then((module) => ({
    default: module.CrmCatalogMappingsPage,
  })),
);
const CrmCatalogStagingPage = lazy(() =>
  import("./pages/crm/catalog-page.tsx").then((module) => ({
    default: module.CrmCatalogStagingPage,
  })),
);
const CrmCatalogReviewsPage = lazy(
  () => import("./pages/crm/catalog-reviews-page.tsx"),
);
const CrmCatalogPromotionPage = lazy(
  () => import("./pages/crm/catalog-promotion-page.tsx"),
);
const CrmCatalogUpdatesPage = lazy(
  () => import("./pages/crm/catalog-updates-page.tsx"),
);
const CrmCatalogOffersPage = lazy(
  () => import("./pages/crm/catalog-offers-page.tsx"),
);
const CrmCatalogProductsPage = lazy(
  () => import("./pages/crm/catalog-products-page.tsx"),
);
const CrmCatalogSegmentsPage = lazy(
  () => import("./pages/crm/catalog-segments-page.tsx"),
);
const CrmCatalogChannelsPage = lazy(
  () => import("./pages/crm/catalog-channels-page.tsx"),
);
const AuthCallback = lazy(() => import("./pages/auth/Callback.tsx"));
const LoginPage = lazy(() => import("./pages/auth/login-page.tsx"));
const TwoFactorPage = lazy(() => import("./pages/auth/two-factor-page.tsx"));
const ResetPasswordPage = lazy(
  () => import("./pages/auth/reset-password-page.tsx"),
);
const UnsubscribePage = lazy(() => import("./pages/unsubscribe/page.tsx"));

// Public secondary pages: lazily loaded per route so the initial bundle only
// carries the home page. Each is fetched when its route is first visited.
const ServicesPage = lazy(() => import("./pages/services/page.tsx"));
const AboutPage = lazy(() => import("./pages/about/page.tsx"));
const CareersPage = lazy(() => import("./pages/careers/page.tsx"));
const ServiceDetailPage = lazy(
  () => import("./pages/services/detail-page.tsx"),
);
const SponsoredPage = lazy(() => import("./pages/sponsored/page.tsx"));
const EditorialPage = lazy(() => import("./pages/editorial/page.tsx"));
const PortfolioPage = lazy(() => import("./pages/portfolio/page.tsx"));
const PortfolioDetailPage = lazy(
  () => import("./pages/portfolio/detail-page.tsx"),
);
const PartnersPage = lazy(() => import("./pages/partners/page.tsx"));
const BlogPage = lazy(() => import("./pages/blog/page.tsx"));
const ArticleDetailPage = lazy(() => import("./pages/blog/detail-page.tsx"));
const ShopPage = lazy(() => import("./pages/shop/page.tsx"));
const ClientShopDetailPage = lazy(() => import("./pages/shop/detail-page.tsx"));
const FaqPage = lazy(() => import("./pages/faq/page.tsx"));
const InquiriesPage = lazy(() => import("./pages/inquiries/page.tsx"));
const ToolsPage = lazy(() => import("./pages/tools/page.tsx"));
const SearchPage = lazy(() => import("./pages/search/page.tsx"));
const ToolDetailPage = lazy(() => import("./pages/tools/detail-page.tsx"));
const EnergyComparatorToolPage = lazy(
  () => import("./pages/tools/energy-comparator-page.tsx"),
);
const PublicSupportTicketPage = lazy(
  () => import("./pages/contact/ticket-detail-page.tsx"),
);
const ForumPostPage = lazy(() => import("./pages/forum/detail-page.tsx"));
const ForumIndexPage = lazy(() => import("./pages/forum/index-page.tsx"));
const ForumCategoryPage = lazy(() => import("./pages/forum/category-page.tsx"));
const CrmForumModerationPage = lazy(
  () => import("./pages/crm/forum-moderation-page.tsx"),
);
const LegalPage = lazy(() => import("./pages/legal/legal-page.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

// Landing params (eg ?ref=, ?utm_source=) arrive on `/` and are read by pages
// rendered under /:lng, so the root redirect must carry search and hash across.
function RootRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={setLocaleInPath(
        SAVED_OR_DEFAULT_LOCALE,
        "/",
        location.search,
        location.hash,
      )}
      replace
    />
  );
}

function PublicSupportRedirect() {
  return (
    <Navigate to={localizedPath("contact", SAVED_OR_DEFAULT_LOCALE)} replace />
  );
}

// Collect the unique set of slugs used by a route key across all locales, so a
// single element renders regardless of which localized permalink was visited.
function uniqueSlugs(key: RouteKey): string[] {
  const slugs = SUPPORTED_LOCALES_ARRAY.map(
    (loc) => ROUTE_SLUGS[key][loc],
  ).filter(Boolean);
  return Array.from(new Set(slugs));
}

// Legal pages: each renders LegalPage with its namespace/route key.
const LEGAL_ROUTES: {
  key: RouteKey;
  ns: "privacy" | "cookie" | "terms" | "withdrawal";
}[] = [
  { key: "privacy", ns: "privacy" },
  { key: "cookie", ns: "cookie" },
  { key: "terms", ns: "terms" },
  { key: "withdrawal", ns: "withdrawal" },
];

export default function App() {
  useServiceWorker();
  return (
    <DefaultProviders>
      <BrowserRouter>
        <RuntimeNavigationObserver />
        <BrandFavicon />
        <ImpersonationBanner />
        <PublicIntegrations />
        <NetworkAnalytics />
        <Suspense fallback={<RuntimeLoadingFallback />}>
          <Routes>
            <Route path="/support-chat" element={<SupportWidgetPage />} />
            {/* Root: redirect to saved/default locale */}
            <Route path="/" element={<RootRedirect />} />

            {/* Non-localized routes (auth, webhooks, etc.) */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/two-factor" element={<TwoFactorPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/unsubscribe/:lng"
              element={
                <LocaleWrapper>
                  <UnsubscribePage />
                </LocaleWrapper>
              }
            />

            {/* Unified private area: role-aware customer, team and admin workspaces. */}
            <Route
              path="/crm"
              element={
                <CrmGuard allow={["admin", "staff", "client"]}>
                  <CrmLayout />
                </CrmGuard>
              }
            >
              <Route
                index
                element={
                  <CrmGuard
                    allow={["admin", "staff", "client"]}
                    permission="dashboard.read"
                  >
                    <CrmDashboardPage />
                  </CrmGuard>
                }
              />
              <Route
                path="my-services"
                element={
                  <CrmGuard allow={["client"]}>
                    <CrmClientServicesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="my-projects"
                element={
                  <CrmGuard allow={["client"]}>
                    <CrmClientProjectsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="my-billing"
                element={<Navigate to="/crm/wallet" replace />}
              />
              <Route path="support" element={<PublicSupportRedirect />} />
              <Route
                path="leads"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="contacts.read"
                  >
                    <CrmLeadsPage />
                  </CrmGuard>
                }
              />
              <Route path="statistiche" element={<CrmGuard allow={["admin", "staff", "client"]} permission="analytics.read"><CrmNetworkAnalyticsPage /></CrmGuard>} />
              <Route path="software" element={<CrmGuard allow={["admin","staff"]} permission="softwareCatalog.read"><CrmSoftwarePage /></CrmGuard>} />
              <Route path="secretariat" element={<CrmGuard allow={["admin", "staff"]}><ContactRequestsPage secretariat /></CrmGuard>} />
              <Route path="contact-requests" element={<CrmGuard allow={["admin", "staff"]}><ContactRequestsPage /></CrmGuard>} />
              <Route
                path="applications"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="applications.read"
                  >
                    <CrmJobApplicationsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="tools"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="tools.read">
                    <CrmToolsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="clients"
                element={<Navigate to="/crm/leads?tab=clients" replace />}
              />
              <Route
                path="clients/:id"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="companies.read"
                  >
                    <CrmCompanyDetailPage />
                  </CrmGuard>
                }
              />
              <Route
                path="companies"
                element={<Navigate to="/crm/leads?tab=clients" replace />}
              />
              <Route
                path="companies/:id"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="companies.read"
                  >
                    <CrmCompanyDetailPage />
                  </CrmGuard>
                }
              />
              <Route
                path="projects"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="websiteProjects.read"
                  >
                    <CrmProjectsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="projects/:id"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="websiteProjects.read">
                    <CrmWebsiteProjectDetailPage />
                  </CrmGuard>
                }
              />
              <Route
                path="website-projects"
                element={<Navigate to="/crm/projects" replace />}
              />
              <Route
                path="tasks"
                element={<Navigate to="/crm/production" replace />}
              />
              <Route
                path="production"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permissionsAny={["copywriting.read", "tasks.read"]}
                  >
                    <CrmProductionPage />
                  </CrmGuard>
                }
              />
              <Route
                path="partner-contracts"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="partnerContracts.read"
                  >
                    <CrmPartnerContractsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="energy-comparator"
                element={
                  <CrmGuard allow={["admin", "staff", "client"]}>
                    <CrmEnergyComparatorAccessPage />
                  </CrmGuard>
                }
              />
              <Route
                path="tools/:toolId"
                element={
                  <CrmGuard allow={["admin", "staff", "client"]}>
                    <CrmCustomerToolPage />
                  </CrmGuard>
                }
              />
              <Route
                path="orders"
                element={
                  <CrmGuard allow={["admin", "staff", "client"]}>
                    <CrmCustomerOrdersPage />
                  </CrmGuard>
                }
              />
              <Route
                path="license-deliveries"
                element={
                  <CrmGuard allow={["admin", "staff", "client"]}>
                    <CrmSoftwareLicenseDeliveriesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="technical-assistance"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="technicalAssistance.read"
                  >
                    <CrmTechnicalAssistancePage />
                  </CrmGuard>
                }
              />
              <Route
                path="commerce"
                element={<Navigate to="/crm/catalog" replace />}
              />
              <Route
                path="products"
                element={<Navigate to="/crm" replace />}
              />
              <Route
                path="technical-orders"
                element={<Navigate to="/crm" replace />}
              />
              <Route
                path="digital-assistance"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="digitalAssistance.read"
                  >
                    <CrmDigitalAssistancePage />
                  </CrmGuard>
                }
              />
              <Route
                path="wallet"
                element={
                  <CrmGuard allow={["admin", "staff", "client"]}>
                    <CrmWalletPage />
                  </CrmGuard>
                }
              />
              <Route
                path="calendar"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="calendar.read"
                  >
                    <CrmCalendarPage />
                  </CrmGuard>
                }
              />
              <Route
                path="chat"
                element={
                  <CrmGuard allow={["admin", "staff"]}>
                    <CrmChatPage />
                  </CrmGuard>
                }
              />
              <Route
                path="quotes"
                element={<Navigate to="/crm/wallet" replace />}
              />
              <Route
                path="invoices"
                element={<Navigate to="/crm/wallet" replace />}
              />
              <Route path="tickets" element={<PublicSupportRedirect />} />
              <Route
                path="forum-moderation"
                element={<PublicSupportRedirect />}
              />
              <Route path="tickets/:id" element={<PublicSupportRedirect />} />
              <Route
                path="users"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="users.read">
                    <CrmUsersPage />
                  </CrmGuard>
                }
              />
              <Route
                path="roles"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="users.manage"
                  >
                    <CrmRolesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="integrations"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="settings.read"
                  >
                    <CrmIntegrationsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmCmsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/texts"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmContentTextsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/texts/:namespace"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmContentTextsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/seo"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmCmsSeoPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/seo/metadata"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmContentSeoMetadataPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/seo/metadata/:namespace"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmContentSeoMetadataPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/seo/redirects"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmCmsRedirectsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="content/forum"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmCmsForumPage />
                  </CrmGuard>
                }
              />
              <Route
                path="hero-animation"
                element={
                  <CrmGuard allow={["admin", "staff"]} permission="cms.read">
                    <CrmHeroAnimationPage />
                  </CrmGuard>
                }
              />
              <Route
                path="theme"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="settings.read"
                  >
                    <CrmThemePage />
                  </CrmGuard>
                }
              />
              <Route
                path="portfolio"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="portfolio.read"
                  >
                    <CrmPortfolioPage />
                  </CrmGuard>
                }
              />
              <Route
                path="shop-products"
                element={<Navigate to="/crm/catalog" replace />}
              />
              <Route
                path="services"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="services.read"
                  >
                    <CrmServicesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="client-shops"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="services.read"
                  >
                    <CrmClientShopsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="settings"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="settings.read"
                  >
                    <CrmSettingsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.dashboard.read"
                  >
                    <CrmCatalogPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/suppliers"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.suppliers.read"
                  >
                    <CrmCatalogSuppliersPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/products"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.products.read"
                  >
                    <CrmCatalogProductsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/offers"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.offers.read_private"
                  >
                    <CrmCatalogOffersPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/segments"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.segments.read"
                  >
                    <CrmCatalogSegmentsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/channels"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.integrations.manage"
                  >
                    <CrmCatalogChannelsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/categories"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.categories.read"
                  >
                    <CrmCatalogCategoriesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/taxonomies"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.mappings.read"
                  >
                    <CrmCatalogTaxonomiesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/mappings"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.mappings.read"
                  >
                    <CrmCatalogMappingsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/staging"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.imports.read"
                  >
                    <CrmCatalogStagingPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/reviews"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.products.review"
                  >
                    <CrmCatalogReviewsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/promotion"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="catalog.products.promote"
                  >
                    <CrmCatalogPromotionPage />
                  </CrmGuard>
                }
              />
              <Route
                path="catalog/updates"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permissions={[
                      "catalog.pricing.read",
                      "catalog.offers.read_private",
                    ]}
                  >
                    <CrmCatalogUpdatesPage />
                  </CrmGuard>
                }
              />
              <Route
                path="settings/legacy"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="settings.manage"
                  >
                    <CrmMigrationPage />
                  </CrmGuard>
                }
              />
              <Route
                path="migration"
                element={<Navigate to="/crm/settings/legacy" replace />}
              />
              <Route
                path="finance"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="finance.read"
                  >
                    <CrmFinancePage />
                  </CrmGuard>
                }
              />
              <Route
                path="revenue"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="finance.read"
                  >
                    <CrmRevenuePage />
                  </CrmGuard>
                }
              />
              <Route
                path="fatture-srv"
                element={<Navigate to="/crm/projects" replace />}
              />
              <Route
                path="liquidations"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="finance.read"
                  >
                    <CrmPayoutsPage />
                  </CrmGuard>
                }
              />
              <Route
                path="payments"
                element={<Navigate to="/crm/revenue" replace />}
              />
              <Route
                path="training"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="training.read"
                  >
                    <CrmTrainingPage />
                  </CrmGuard>
                }
              />
              <Route
                path="coworking"
                element={
                  <CrmGuard
                    allow={["admin", "staff"]}
                    permission="coworking.read"
                  >
                    <CrmCoworkingPage />
                  </CrmGuard>
                }
              />
            </Route>

            <Route path="/crm/api/software/:appId/verifica" element={<SiteLayout/>}><Route index element={<SoftwarePublicPage/>}/></Route>
            <Route path="/crm/api/pcdiag/verifica" element={<SiteLayout/>}><Route index element={<SoftwarePublicPage/>}/></Route>
            <Route path="/software/:appId/legal" element={<SiteLayout/>}><Route index element={<SoftwarePublicPage/>}/><Route path=":file" element={<SoftwarePublicPage/>}/></Route>
            {/* All localized routes under /:lng */}
            <Route
              path="/:lng"
              element={
                <LocaleWrapper>
                  <SiteLayout />
                </LocaleWrapper>
              }
            >
              <Route index element={<HomePage />} />
              {['software','logiciels'].flatMap(slug=>[<Route key={slug+'-legal'} path={`${slug}/:appId/legal`} element={<SoftwarePublicPage/>}/>,<Route key={slug+'-legal-file'} path={`${slug}/:appId/legal/:file`} element={<SoftwarePublicPage/>}/>])}
              {['software','logiciels'].flatMap(slug=>[<Route key={slug} path={slug} element={<SoftwarePublicPage/>}/>,<Route key={slug+'-detail'} path={`${slug}/:appId`} element={<SoftwarePublicPage/>}/>])}

              {uniqueSlugs("about").map((slug) => (
                <Route
                  key={`about-${slug}`}
                  path={slug}
                  element={<AboutPage />}
                />
              ))}

              {uniqueSlugs("careers").map((slug) => (
                <Route
                  key={`careers-${slug}`}
                  path={slug}
                  element={<CareersPage />}
                />
              ))}

              {/* Services: overview + detail, for each localized slug */}
              {uniqueSlugs("services").flatMap((servicesSlug) => [
                <Route
                  key={`services-${servicesSlug}`}
                  path={servicesSlug}
                  element={<ServicesPage />}
                />,
                <Route
                  key={`service-detail-${servicesSlug}`}
                  path={`${servicesSlug}/:slug`}
                  element={<ServiceDetailPage />}
                />,
              ])}

              {/* Portfolio: one route per unique localized slug */}
              {uniqueSlugs("portfolio").map((slug) => (
                <Route key={`portfolio-${slug}`} path={slug}>
                  <Route index element={<PortfolioPage />} />
                  <Route path=":slug" element={<PortfolioDetailPage />} />
                </Route>
              ))}

              {/* Partners: one route per unique localized slug */}
              {uniqueSlugs("partners").map((slug) => (
                <Route
                  key={`partners-${slug}`}
                  path={slug}
                  element={<PartnersPage />}
                />
              ))}

              {/* Sponsored articles: one route per unique localized slug */}
              {uniqueSlugs("editorial").map((slug) => (
                <Route
                  key={`editorial-${slug}`}
                  path={slug}
                  element={<EditorialPage />}
                />
              ))}

              {/* Sponsored articles: one route per unique localized slug */}
              {uniqueSlugs("sponsored").map((slug) => (
                <Route
                  key={`sponsored-${slug}`}
                  path={slug}
                  element={<SponsoredPage />}
                />
              ))}

              {/* Magazine: overview + article detail, for each localized slug */}
              {uniqueSlugs("blog").flatMap((slug) => [
                <Route
                  key={`blog-${slug}`}
                  path={slug}
                  element={<BlogPage />}
                />,
                <Route
                  key={`blog-detail-${slug}`}
                  path={`${slug}/:source/:id`}
                  element={<ArticleDetailPage />}
                />,
              ])}

              {/* Shop: overview + client shop detail, for each localized slug */}
              {uniqueSlugs("shop").flatMap((slug) => [
                <Route
                  key={`shop-${slug}`}
                  path={slug}
                  element={<ShopPage />}
                />,
                <Route
                  key={`shop-detail-${slug}`}
                  path={`${slug}/:slug`}
                  element={<ClientShopDetailPage />}
                />,
              ])}

              {/* Interactive tools: localized index and dedicated detail pages. */}
              {uniqueSlugs("tools").flatMap((slug) => [
                <Route
                  key={`tools-${slug}`}
                  path={slug}
                  element={<ToolsPage />}
                />,
                <Route
                  key={`tool-detail-${slug}`}
                  path={`${slug}/:slug`}
                  element={<ToolDetailPage />}
                />,
                ...Array.from(
                  new Set(Object.values(ENERGY_COMPARATOR_SLUGS)),
                ).map((energySlug) => (
                  <Route
                    key={`energy-comparator-${slug}-${energySlug}`}
                    path={`${slug}/${energySlug}`}
                    element={<EnergyComparatorToolPage />}
                  />
                )),
              ])}

              {uniqueSlugs("search").map((slug) => (
                <Route
                  key={`search-${slug}`}
                  path={slug}
                  element={<SearchPage />}
                />
              ))}

              {uniqueSlugs("faq").map((slug) => (
                <Route key={`faq-${slug}`} path={slug} element={<FaqPage />} />
              ))}

              {uniqueSlugs("inquiries").map((slug) => (
                <Route
                  key={`inquiries-${slug}`}
                  path={slug}
                  element={<InquiriesPage />}
                />
              ))}

              {/* Contact + community forum: one route set per unique contact slug */}
              {uniqueSlugs("contact").flatMap((slug) => [
                <Route
                  key={`contact-${slug}`}
                  path={slug}
                  element={<ForumIndexPage />}
                />,
                <Route
                  key={`forum-${slug}`}
                  path={`${slug}/${FORUM_SUBPATH}`}
                  element={<Navigate to=".." replace />}
                />,
                <Route
                  key={`forum-category-legacy-${slug}`}
                  path={`${slug}/${FORUM_SUBPATH}/categoria/:category`}
                  element={<ForumCategoryPage />}
                />,
                <Route
                  key={`forum-category-post-legacy-${slug}`}
                  path={`${slug}/${FORUM_SUBPATH}/categoria/:category/:id`}
                  element={<ForumPostPage />}
                />,
                <Route
                  key={`forum-post-old-${slug}`}
                  path={`${slug}/${FORUM_SUBPATH}/discussione/:id`}
                  element={<ForumPostPage />}
                />,
                <Route
                  key={`forum-category-${slug}`}
                  path={`${slug}/categoria/:category`}
                  element={<ForumCategoryPage />}
                />,
                <Route
                  key={`forum-category-post-${slug}`}
                  path={`${slug}/categoria/:category/:id`}
                  element={<ForumPostPage />}
                />,
                <Route
                  key={`forum-post-${slug}`}
                  path={`${slug}/discussione/:id`}
                  element={<ForumPostPage />}
                />,
                <Route
                  key={`forum-moderation-${slug}`}
                  path={`${slug}/moderazione`}
                  element={
                    <CrmGuard
                      allow={["admin", "staff"]}
                      permission="tickets.read"
                    >
                      <CrmForumModerationPage />
                    </CrmGuard>
                  }
                />,
                <Route
                  key={`support-ticket-${slug}`}
                  path={`${slug}/ticket/:id`}
                  element={
                    <CrmGuard allow={["admin", "staff"]}>
                      <PublicSupportTicketPage />
                    </CrmGuard>
                  }
                />,
              ])}

              {LEGAL_ROUTES.flatMap(({ key, ns }) =>
                uniqueSlugs(key).map((slug) => (
                  <Route
                    key={`${key}-${slug}`}
                    path={slug}
                    element={<LegalPage ns={ns} routeKey={key} />}
                  />
                )),
              )}

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </DefaultProviders>
  );
}
